class NewsletterPopup extends HTMLElement {
  constructor() {
    super();

    this.openTimer = undefined;
    this.handleClose = this.handleClose.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.dialog = this.querySelector('.newsletter-popup__dialog');
    this.reminder = this.querySelector('[data-newsletter-popup-open]');
    this.closeButtons = this.querySelectorAll('[data-newsletter-popup-close]');
    this.dismissButtons = this.querySelectorAll('[data-newsletter-popup-dismiss]');
    this.openButtons = this.querySelectorAll('[data-newsletter-popup-open]');

    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
    this.dismissButtons.forEach((button) => button.addEventListener('click', this.handleDismiss));
    this.openButtons.forEach((button) => button.addEventListener('click', this.handleOpen));
    document.addEventListener('keydown', this.handleKeydown);

    if (this.querySelector('.newsletter-popup__message--success, .newsletter-popup__message--error')) {
      this.open();
      return;
    }

    if (this.shouldAutoOpen()) {
      this.scheduleOpen();
      return;
    }

    this.showReminder();
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
    this.dismissButtons?.forEach((button) => button.removeEventListener('click', this.handleDismiss));
    this.openButtons?.forEach((button) => button.removeEventListener('click', this.handleOpen));
    document.removeEventListener('keydown', this.handleKeydown);
  }

  get cookieName() {
    return this.dataset.cookieName || 'newsletter-popup';
  }

  get popupSeconds() {
    const seconds = Number.parseInt(this.dataset.popupSeconds || '30', 10);
    return Number.isNaN(seconds) ? 30 : seconds;
  }

  get popupDays() {
    const days = Number.parseInt(this.dataset.popupDays || '30', 10);
    return Number.isNaN(days) ? 30 : Math.max(days, 0);
  }

  get storageKey() {
    return `${this.cookieName}:dismissed-until`;
  }

  get isDesignMode() {
    return this.dataset.designMode === 'true' || document.documentElement.classList.contains('shopify-design-mode');
  }

  shouldAutoOpen() {
    return this.isDesignMode || !this.hasDismissal();
  }

  scheduleOpen() {
    const delay = this.isDesignMode ? 0 : this.popupSeconds * 1000;

    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => this.open(), delay);
  }

  handleOpen() {
    this.open();
  }

  handleClose() {
    this.close();
  }

  handleDismiss() {
    if (this.classList.contains('is-open')) {
      this.close();
      return;
    }

    this.dismiss();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  open() {
    window.clearTimeout(this.openTimer);
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');

    if (this.reminder) {
      this.reminder.setAttribute('aria-hidden', 'true');
    }

    this.dialog?.focus({ preventScroll: true });
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.dismiss();
    this.showReminder();
  }

  showReminder() {
    if (this.reminder) {
      this.reminder.setAttribute('aria-hidden', 'false');
    }
  }

  createCookie() {
    const maxAge = this.popupDays * 24 * 60 * 60;
    const expiresAt = new Date(Date.now() + maxAge * 1000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${encodeURIComponent(this.cookieName)}=dismissed; max-age=${maxAge}; expires=${expiresAt}; path=/; SameSite=Lax${secure}`;
  }

  createStorageDismissal() {
    try {
      const expiresAt = Date.now() + this.popupDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.storageKey, JSON.stringify({ expiresAt }));
    } catch (error) {
      // Storage can be unavailable in strict/private browser modes.
    }
  }

  dismiss() {
    this.createCookie();
    this.createStorageDismissal();
  }

  hasDismissal() {
    return this.hasCookie() || this.hasStorageDismissal();
  }

  hasCookie() {
    const encodedName = encodeURIComponent(this.cookieName);

    return document.cookie.split(';').some((cookie) => {
      const cookieName = cookie.trim().split('=')[0];

      return cookieName === encodedName || cookieName === this.cookieName;
    });
  }

  hasStorageDismissal() {
    try {
      const value = window.localStorage.getItem(this.storageKey);
      if (!value) return false;

      const dismissal = JSON.parse(value);
      const expiresAt = Number(dismissal?.expiresAt);

      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        window.localStorage.removeItem(this.storageKey);
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
